import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import Pagination from '../components/ui/Pagination';
import { getOrganizations } from '../services/organizationService';
import { formatRelativeTime } from '../utils/dateUtils';

const PLAN_BADGE: Record<string, { variant: 'default' | 'info' | 'success'; label: string }> = {
  FREE: { variant: 'default', label: 'FREE' },
  PRO: { variant: 'info', label: 'PRO' },
  ENTERPRISE: { variant: 'success', label: 'ENTERPRISE' },
};

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [planType, setPlanType] = useState('');
  const [isActive, setIsActive] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'organizations', { page, search, planType, isActive, sortBy }],
    queryFn: () => getOrganizations({ page, limit: 20, search: search || undefined, planType: planType || undefined, isActive: isActive || undefined, sortBy }),
  });

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-gray-900">조직 관리</h1>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="조직명 / 슬러그 검색"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <select value={planType} onChange={(e) => { setPlanType(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">전체 플랜</option>
          <option value="FREE">FREE</option><option value="PRO">PRO</option><option value="ENTERPRISE">ENTERPRISE</option>
        </select>
        <select value={isActive} onChange={(e) => { setIsActive(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">전체 상태</option><option value="true">활성</option><option value="false">비활성</option>
        </select>
        <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
          <option value="createdAt">가입일순</option><option value="memberCount">멤버순</option>
          <option value="storageUsedBytes">스토리지순</option><option value="lastActivityAt">활동순</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner className="size-8" /></div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">조직명</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">플랜</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">멤버</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase w-40">스토리지</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">가입일</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">최근 활동</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">조직이 없습니다.</td></tr>
                ) : data?.items.map((org) => {
                  const plan = PLAN_BADGE[org.planType] ?? PLAN_BADGE.FREE;
                  const rawPct = org.storageLimitBytes > 0 ? (org.storageUsedBytes / org.storageLimitBytes) * 100 : 0;
                  const pct = Math.round(rawPct);
                  const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-500' : 'bg-primary-500';
                  return (
                    <tr key={org.id} onClick={() => navigate(`/organizations/${org.id}`)} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-4 py-3"><div className="text-sm font-medium text-gray-900">{org.name}</div><div className="text-sm text-gray-400">{org.slug}</div></td>
                      <td className="px-4 py-3"><Badge variant={plan.variant}>{plan.label}</Badge></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{org.memberCount}/{org.planType === 'FREE' ? '5' : '∞'}</td>
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.max(rawPct > 0 ? 1 : 0, Math.min(pct, 100))}%` }} /></div><span className="text-sm text-gray-500 w-8 text-right">{rawPct > 0 && pct === 0 ? '<1' : pct}%</span></div></td>
                      <td className="px-4 py-3 text-sm text-gray-500">{new Date(org.createdAt).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{org.lastActivityAt ? formatRelativeTime(org.lastActivityAt) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data && <Pagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />}
        </>
      )}
    </div>
  );
}
